import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Nav } from "./Nav";
import { Footer } from "./Footer";
import { TextAndrea } from "./TextAndrea";
import { Reveal } from "./Reveal";
import { ContactFallback } from "./ContactFallback";
import { ARTICLES, GUIDE_CLUSTERS, GUIDE_DESCRIPTIONS } from "../pages/articles";
import { usePageMeta, useBreadcrumbSchema } from "../lib/seo";
import { trackEvent } from "../lib/tracking";
import { CALENDLY_URL } from "../lib/contact";

// /sleep-guides — the crawlable index for the problem-intent article
// cluster, grouped by stage. Every article links here; every article links
// to its stage page. Nothing is orphaned.
const CRUMBS = [
  { label: "Home", href: "/" },
  { label: "Sleep Guides" },
];

export const GuidesIndex = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  usePageMeta({
    title: "Sleep Guides for Real Nights | The Nurtured Parent Co.",
    description:
      "Real answers for the 2 a.m. searches — newborn day/night confusion, 30-minute naps, night waking, toddler bedtimes, and when to bring in help. From a certified pediatric sleep consultant.",
    path: "/sleep-guides",
  });
  useBreadcrumbSchema(CRUMBS);

  return (
    <div className="bg-ivory text-ink antialiased" data-testid="guides-index">
      <Nav home={false} open={menuOpen} setOpen={setMenuOpen} />
      <main>
        <header className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-32 lg:pt-40 pb-14 lg:pb-16">
          <nav aria-label="Breadcrumb" data-testid="guides-breadcrumbs" className="mb-12">
            <ol className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {CRUMBS.map((c, i) => (
                <li key={c.label} className="flex items-center gap-3 text-[11px] tracking-[0.16em] uppercase font-semibold">
                  {i > 0 && <span aria-hidden="true" className="text-clay/60">&rsaquo;</span>}
                  {c.href ? (
                    <a href={c.href} className="text-smoke/80 hover:text-ink transition-colors duration-300">
                      {c.label}
                    </a>
                  ) : (
                    <span aria-current="page" className="text-ink/70">{c.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-euca font-semibold">Sleep guides</p>
            <h1 data-testid="guides-h1" className="mt-7 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-ink max-w-4xl">
              Real answers for the
              <span className="italic text-clay"> 2 a.m. searches.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base sm:text-lg font-normal leading-relaxed text-smoke">
              The questions parents actually type into the search bar at
              night — answered honestly, without a method being sold at the
              end. When you&rsquo;d rather have someone look at your
              child&rsquo;s whole picture, that&rsquo;s what I&rsquo;m here
              for.
            </p>
          </Reveal>
        </header>

        {GUIDE_CLUSTERS.map((cluster) => (
          <section key={cluster} className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pb-14 lg:pb-20">
            <Reveal>
              <h2 className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">{cluster}</h2>
              <div data-testid={`guide-cluster-${cluster.replace(/\W+/g, "-").toLowerCase()}`} className="mt-6 border-t border-ink/10">
                {Object.values(ARTICLES)
                  .filter((a) => a.cluster === cluster && !a.draft)
                  .map((a) => (
                    <a
                      key={a.path}
                      data-testid={`guide-link-${a.path.split("/").pop()}`}
                      href={a.path}
                      className="group grid lg:grid-cols-12 gap-1.5 lg:gap-6 py-6 border-b border-ink/10 items-baseline"
                    >
                      <span className="lg:col-span-5 font-serif font-light text-xl sm:text-2xl text-ink group-hover:text-claydark transition-colors duration-300">
                        {a.h1.join(" ")}
                      </span>
                      <span className="lg:col-span-6 text-sm sm:text-base font-normal text-smoke leading-relaxed">
                        {GUIDE_DESCRIPTIONS[a.path.split("/").pop()]}
                      </span>
                      <span className="hidden lg:block lg:col-span-1 lg:text-right text-claydark">
                        <ArrowRight size={16} className="inline transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                      </span>
                    </a>
                  ))}
              </div>
            </Reveal>
          </section>
        ))}

        <section className="bg-claysoft">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-20 lg:py-24">
            <Reveal>
              <h2 className="font-serif font-light text-3xl sm:text-4xl leading-[1.12] tracking-tight text-ink max-w-3xl">
                Reading about it only gets a night so far.
              </h2>
              <p className="mt-5 max-w-xl text-base sm:text-lg font-normal leading-relaxed text-smoke">
                When you&rsquo;d rather have someone look at your child&rsquo;s
                whole picture — that&rsquo;s what the complimentary pediatric
                sleep assessment is for.
              </p>
              <a
                data-testid="guides-book-cta"
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("cta_click", { location: "guides_index" });
                  trackEvent("book_assessment_click", { location: "guides_index" });
                }}
                className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-ink text-ivory text-sm sm:text-base font-medium px-8 py-4 hover:bg-espresso transition-colors duration-300"
              >
                Book my complimentary pediatric sleep assessment
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <TextAndrea typing={false} flowOpen={false} onStart={() => window.location.assign("/#talk-with-andrea")} />
      <Footer home={false} />
      <ContactFallback />
    </div>
  );
};
